// Small presentational building blocks shared by the new pages.
import { useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Plus } from "lucide-react";
import { SmartLink } from "@/components/Header";
import { integrationByKey, integrations as allIntegrations, type Integration } from "@/data/site";
import { productPath, type Product } from "@/data/products";
import { Helmet, HelmetProvider } from "react-helmet-async";

export const SectionHeading = ({
  eyebrow,
  title,
  lead,
  align = "left",
}: {
  eyebrow?: string;
  title: ReactNode;
  lead?: ReactNode;
  align?: "left" | "center";
}) => (
  <div className={align === "center" ? "mx-auto max-w-2xl text-center" : "max-w-2xl"}>
    {eyebrow && <p className="cm-eyebrow mb-3">{eyebrow}</p>}
    <h2 className="cm-h2">{title}</h2>
    {lead && <p className="cm-lead mt-4">{lead}</p>}
  </div>
);

const statusLabel: Record<Product["status"], string | null> = {
  live: null,
  beta: "Beta",
  "coming-soon": "Coming soon",
};

// A schematic of the product UI drawn in markup, so cards stay crisp at any size
// and new products get a visual without anyone opening a design tool.
const previewRows = [
  { from: "Dana · Acme Co", line: "Invoice #2041 is due Friday", tag: "Priority" },
  { from: "#launch", line: "Summary: 3 decisions, 1 blocker", tag: "Summary" },
  { from: "Priya", line: "Can we meet Thursday at 3pm?", tag: "Meeting" },
  { from: "Support", line: "Do you ship to Canada?", tag: "FAQ reply" },
  { from: "Leo · Northwind", line: "Contract looks good, one question", tag: "Draft ready" },
];

const inboxPanel = {
  heading: "AI summary",
  body: "Acme's invoice is due Friday. The launch channel settled on three decisions and has one blocker waiting on you.",
  subheading: "Suggested reply",
  box: "Thanks Dana, I'll have this paid by Thursday.",
  primary: "Send",
  secondary: "Edit",
};

export const ProductPreview = ({ product, large = false }: { product: Product; large?: boolean }) => {
  const logos = product.integrations.map(integrationByKey).filter(Boolean) as Integration[];
  const rows = (product.preview?.rows ?? previewRows).slice(0, large ? 5 : 3);
  const nav = product.preview?.nav ?? ["Home", "Summaries", "Priority Hub", "Calendar", "Workflows"];
  const panel = product.preview?.panel ?? inboxPanel;
  return (
    <div aria-hidden className="relative overflow-hidden rounded-lg border border-border bg-background">
      <div className="flex h-8 items-center gap-1.5 border-b border-border bg-[hsl(40_9%_98%)] px-3">
        <span className="h-2 w-2 rounded-full bg-[hsl(0_0%_84%)]" />
        <span className="h-2 w-2 rounded-full bg-[hsl(0_0%_84%)]" />
        <span className="h-2 w-2 rounded-full bg-[hsl(0_0%_84%)]" />
        <span className="ml-3 truncate text-[10px] text-muted-foreground">{product.name}</span>
      </div>
      <div className={`grid ${large ? "grid-cols-[104px_1fr] sm:grid-cols-[150px_1fr] lg:grid-cols-[170px_1fr_260px]" : "grid-cols-[92px_1fr] sm:grid-cols-[120px_1fr]"}`}>
        <div className="space-y-1 border-r border-border bg-[hsl(40_9%_98%)] p-2.5 sm:p-3">
          {nav.slice(0, large ? 5 : 4).map((l, i) => (
            <div key={l} className={`truncate rounded px-1.5 py-1 ${large ? "text-[11px] sm:text-xs" : "text-[10px]"} ${i === 1 ? "bg-[hsl(40_6%_91%)] font-medium text-foreground" : "text-muted-foreground"}`}>
              {l}
            </div>
          ))}
        </div>
        <div className={`min-w-0 space-y-2 ${large ? "p-3 sm:p-4" : "p-3"}`}>
          {rows.map((r, i) => (
            <div key={r.line} className="flex items-center gap-2.5 rounded-md border border-border bg-background px-2.5 py-2">
              {(() => {
                const rowApp = "app" in r ? (r.app ? integrationByKey(r.app) : null) : logos[i % logos.length];
                return rowApp ? <img src={rowApp.logo} alt="" className="h-4 w-4 shrink-0 object-contain" /> : null;
              })()}
              <div className="min-w-0 flex-1">
                <p className={`truncate font-medium text-foreground ${large ? "text-[11px] sm:text-xs" : "text-[10px]"}`}>{r.from}</p>
                <p className={`truncate text-muted-foreground ${large ? "text-[11px] sm:text-xs" : "text-[10px]"}`}>{r.line}</p>
              </div>
              <span className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-medium sm:text-[10px] ${i === 0 ? "bg-foreground text-background" : "border border-border text-muted-foreground"}`}>
                {r.tag}
              </span>
            </div>
          ))}
        </div>
        {large && (
          <div className="hidden border-l border-border p-4 lg:block">
            <p className="text-[11px] font-medium text-muted-foreground">{panel.heading}</p>
            <p className="mt-2 text-xs leading-relaxed text-foreground">{panel.body}</p>
            <p className="mt-4 text-[11px] font-medium text-muted-foreground">{panel.subheading}</p>
            <div className="mt-2 break-words rounded-md border border-border bg-[hsl(40_9%_98%)] p-2.5 text-xs leading-relaxed text-foreground">
              {panel.box}
            </div>
            <div className="mt-3 flex gap-1.5">
              <span className="rounded bg-foreground px-2 py-1 text-[10px] font-medium text-background">{panel.primary}</span>
              <span className="rounded border border-border px-2 py-1 text-[10px] text-muted-foreground">{panel.secondary}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const ProductCard = ({ product, featured = false }: { product: Product; featured?: boolean }) => {
  const soon = product.status === "coming-soon";
  const body = (
    <article className={`cm-card group flex h-full min-w-0 flex-col p-5 sm:p-6 ${soon ? "opacity-70" : "cm-card-hover"}`}>
      <div className={featured ? "grid gap-6 md:grid-cols-2 md:items-center" : "flex flex-col gap-5"}>
        <div className={`min-w-0 ${featured ? "order-2 md:order-1" : ""}`}>
          <div className="flex items-center gap-2">
            <span className="cm-tag">{product.category}</span>
            {statusLabel[product.status] && <span className="cm-tag">{statusLabel[product.status]}</span>}
          </div>
          <h3 className="mt-4 text-xl font-semibold tracking-[-0.02em] text-foreground sm:text-2xl">{product.name}</h3>
          <p className="mt-1 text-[15px] font-medium text-foreground">{product.tagline}</p>
          <p className="cm-body mt-3">{product.description}</p>
          <ul className="mt-4 flex flex-wrap gap-1.5">
            {product.highlights.map((h) => (
              <li key={h} className="rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground">{h}</li>
            ))}
          </ul>
          {!soon && (
            <span className="mt-6 inline-flex items-center gap-1.5 text-[15px] font-medium text-foreground">
              Explore {product.name.replace("ClearMakes ", "")}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </span>
          )}
        </div>
        <div className={`min-w-0 ${featured ? "order-1 md:order-2" : ""}`}>
          <ProductPreview product={product} />
        </div>
      </div>
    </article>
  );
  return soon ? body : <SmartLink href={productPath(product)} className="block h-full">{body}</SmartLink>;
};

export const IntegrationsStrip = ({ keys, linkToPages = true }: { keys?: string[]; linkToPages?: boolean }) => {
  const list = keys ? (keys.map(integrationByKey).filter(Boolean) as Integration[]) : allIntegrations;
  return (
    <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {list.map((i) => {
        const inner = (
          <span className="cm-card cm-card-hover flex h-16 items-center gap-3 px-4">
            <img src={i.logo} alt="" className="h-6 w-6 shrink-0 object-contain opacity-80 grayscale transition-[filter,opacity] duration-300 group-hover:opacity-100 group-hover:grayscale-0" loading="lazy" />
            <span className="text-[15px] font-medium text-foreground">{i.name}</span>
            {linkToPages && i.href && <ArrowUpRight className="ml-auto h-4 w-4 text-muted-foreground" />}
          </span>
        );
        return <li key={i.key}>{linkToPages && i.href ? <SmartLink href={i.href} className="block">{inner}</SmartLink> : inner}</li>;
      })}
    </ul>
  );
};

export const FaqList = ({ items }: { items: { q: string; a: string }[] }) => {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="divide-y divide-border border-y border-border">
      {items.map((it, i) => (
        <div key={it.q}>
          <button
            type="button"
            onClick={() => setOpen((o) => (o === i ? null : i))}
            aria-expanded={open === i}
            className="flex w-full items-center justify-between gap-6 py-5 text-left"
          >
            <span className="text-[17px] font-medium text-foreground">{it.q}</span>
            <Plus className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 ${open === i ? "rotate-45" : ""}`} />
          </button>
          <AnimatePresence initial={false}>
            {open === i && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <p className="cm-body max-w-3xl pb-6">{it.a}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

export const CtaBand = ({
  title,
  lead,
  primary,
  secondary,
}: {
  title: string;
  lead?: string;
  primary: { label: string; href: string };
  secondary?: { label: string; href: string };
}) => (
  <section className="cm-section">
    <div className="cm-container">
      <div className="rounded-2xl border border-border bg-[hsl(40_9%_97%)] px-6 py-14 text-center sm:px-12 sm:py-20">
        <h2 className="cm-h2 mx-auto max-w-2xl">{title}</h2>
        {lead && <p className="cm-lead mx-auto mt-4 max-w-xl">{lead}</p>}
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <SmartLink href={primary.href} className="cm-btn-primary cm-btn-lg w-full sm:w-auto">{primary.label}</SmartLink>
          {secondary && <SmartLink href={secondary.href} className="cm-btn-secondary cm-btn-lg w-full sm:w-auto">{secondary.label}</SmartLink>}
        </div>
      </div>
    </div>
  </section>
);

// Link-preview cards (1200x630). Product pages pass their own; everything else uses the company card.
export const OG_COMPANY = "https://clearmakes.com/og-clearmakes-v3.jpg";
export const OG_INBOX = "https://clearmakes.com/og-inbox-v3.jpg";

export const PageMeta = ({ title, description, path, image = OG_COMPANY, imageAlt }: { title: string; description: string; path: string; image?: string; imageAlt?: string }) => {
  const url = `https://clearmakes.com${path}`;
  const alt = imageAlt || title;
  return (
    <HelmetProvider>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:site_name" content="ClearMakes" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={image} />
        <meta property="og:image:secure_url" content={image} />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={alt} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />
        <meta name="twitter:image:alt" content={alt} />
      </Helmet>
    </HelmetProvider>
  );
};
